import { z } from "zod";
import { Currency, Direction, Id, IsoDate, IsoTimestamp, Pence } from "./primitives.js";

/**
 * The settled domain vocabulary from docs/02-data-model.md: the entities as
 * they appear on the wire, and the enums the whole system agrees on.
 *
 * Request shapes — filters, patches, previews — live in `api/`, next to the
 * routes that parse them.
 */

export const HouseholdRole = z.enum(["owner", "member", "viewer"]);

/**
 * What kind of thing the account is. Structural, so exactly one applies, and
 * the half of the old `role` that anything actually branches on — a
 * `credit_card` with no statement imported hides real spending, where a
 * `savings` account with none hides a transfer nobody counted anyway.
 */
export const AccountKind = z.enum(["current", "savings", "credit_card", "mortgage", "investment"]);

/**
 * What the household uses the account for. Several at once is the normal case
 * — a joint current account pays the bills and is spent from, a cash ISA is
 * savings and investment — which is why this is a set and `AccountKind` is
 * not. Empty is meaningful: a mortgage is neither spent from nor saved into.
 */
export const AccountUse = z.enum(["bills", "spending", "savings", "investing"]);

/** The exports the statement parser knows the quirks of. */
export const StatementFormat = z.enum(["generic", "monzo", "nationwide"]);

/**
 * A literal header name, per field, that wins over the parser's built-in
 * aliases — the answer to a bank naming its columns oddly. `amount` and
 * `debit`/`credit` are alternatives: a signed single column, or a pair, never
 * both read at once (see `readAmount` in `app/src/core/statements/parse.ts`).
 */
export const ColumnMap = z.object({
  date: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).optional(),
  amount: z.string().trim().min(1).optional(),
  debit: z.string().trim().min(1).optional(),
  credit: z.string().trim().min(1).optional(),
});

export const CategoryKind = z.enum(["spending", "income", "transfer", "saving"]);

/**
 * Which of the three headline money figures a row falls in.
 *
 * Derived from `CategoryKind` but deliberately not the same enum. There are
 * four kinds and three buckets: `transfer` has no bucket, because a payment the
 * household wrote down twice belongs in none of them. And the names differ
 * because the buckets are what a person reads — "money in" — while the kinds
 * are what a category *is*.
 *
 * The mapping, and the one exception, live in `app/src/core/sql.ts`.
 */
export const MoneyBucket = z.enum(["in", "out", "saving"]);

export const MatchType = z.enum(["contains", "starts_with", "regex", "exact", "amount_range"]);

/** Manual always wins: a rule re-run must never stamp over a human correction. */
export const CategorySource = z.enum(["rule", "manual", "provider", "import"]);

export const TxnStatus = z.enum(["pending", "booked"]);

export const TxnSource = z.enum(["upload", "feed", "manual"]);

export const ConnectionStatus = z.enum([
  "pending",
  "active",
  "expiring",
  "expired",
  "revoked",
  "error",
]);

export const Theme = z.enum(["system", "light", "dark"]);

export const UNCATEGORISED = "Uncategorised";

export const Transaction = z.object({
  id: Id,
  accountId: Id,
  date: IsoDate,
  description: z.string(),
  merchant: z.string().nullable(),
  amountPence: Pence,
  currency: Currency,
  direction: Direction,
  categoryId: Id.nullable(),
  /**
   * Denormalised alongside `categoryId` for query speed, and because the DSL
   * reads far better against names. The two are kept in step on write.
   */
  category: z.string(),
  categorySource: CategorySource,
  /**
   * What this one row counts as, overriding the kind of its category. Null —
   * the normal case — means the category decides.
   *
   * It exists for transfers, which are two different things filed under one
   * name: half of a double entry already in the ledger, or money crossing the
   * edge of what the ledger tracks. The first must not be counted and the second
   * must, and no category can say which a given row is.
   *
   * A **positive** row overridden to `saving` is a draw-down — money taken back
   * out of a pot — because the saving bucket nets both directions.
   */
  kindOverride: CategoryKind.nullable(),
  /**
   * The other leg, when both halves of a transfer are in the ledger. Null means
   * no counterpart was found, which for a transfer is the interesting state: it
   * is money that crossed the household's perimeter and needs an answer.
   */
  counterpartTxnId: Id.nullable(),
  personId: Id.nullable(),
  status: TxnStatus,
  source: TxnSource,
  excluded: z.boolean(),
  notes: z.string().nullable(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
});

export const Account = z.object({
  id: Id,
  /** Stable slug, derived from the label. The query DSL and exports name it. */
  key: z.string(),
  label: z.string(),
  personId: Id.nullable(),
  kind: AccountKind,
  uses: z.array(AccountUse),
  currency: Currency,
  statementFormat: z.string(),
  columnMap: ColumnMap.nullable(),
  currentBalancePence: Pence.nullable(),
  balanceUpdatedAt: IsoTimestamp.nullable(),
  archivedAt: IsoTimestamp.nullable(),
  sortOrder: z.number().int(),
});

export const Category = z.object({
  id: Id,
  name: z.string(),
  kind: CategoryKind,
  /** A token name, never a hex value, so the theme can change it. */
  colour: z.string().nullable(),
  icon: z.string().nullable(),
  sortOrder: z.number().int(),
  archivedAt: IsoTimestamp.nullable(),
  /**
   * Something the household could not stop paying for. Only the plan's stress
   * tests read it: "lose an income" falls back to these and nothing else.
   */
  essential: z.boolean(),
  /** How many transactions carry it. Deleting a category has to say. */
  txnCount: z.number().int(),
});

export const Person = z.object({
  id: Id,
  key: z.string(),
  label: z.string(),
  userId: Id.nullable(),
  sortOrder: z.number().int(),
});

export const Rule = z.object({
  id: Id,
  /** null means any account. */
  accountId: Id.nullable(),
  matchType: MatchType,
  pattern: z.string(),
  categoryId: Id,
  personId: Id.nullable(),
  priority: z.number().int(),
  enabled: z.boolean(),
  /** How often it has actually fired — how you find the dead and the greedy. */
  matchCount: z.number().int(),
  createdAt: IsoTimestamp,
});

export const StatementFile = z.object({
  id: Id,
  accountId: Id.nullable(),
  filename: z.string(),
  uploadedAt: IsoTimestamp,
  uploadedBy: Id.nullable(),
  rowsParsed: z.number().int(),
  rowsImported: z.number().int(),
  rowsSkipped: z.number().int(),
  /** `preview` until it is committed; nothing enters the ledger unseen. */
  status: z.enum(["preview", "imported", "failed", "discarded"]),
});

export type HouseholdRole = z.infer<typeof HouseholdRole>;
export type AccountKind = z.infer<typeof AccountKind>;
export type AccountUse = z.infer<typeof AccountUse>;
export type StatementFormat = z.infer<typeof StatementFormat>;
export type ColumnMap = z.infer<typeof ColumnMap>;
export type CategoryKind = z.infer<typeof CategoryKind>;
export type MoneyBucket = z.infer<typeof MoneyBucket>;
export type MatchType = z.infer<typeof MatchType>;
export type CategorySource = z.infer<typeof CategorySource>;
export type TxnStatus = z.infer<typeof TxnStatus>;
export type TxnSource = z.infer<typeof TxnSource>;
export type ConnectionStatus = z.infer<typeof ConnectionStatus>;
export type Theme = z.infer<typeof Theme>;
export type Transaction = z.infer<typeof Transaction>;
export type Account = z.infer<typeof Account>;
export type Category = z.infer<typeof Category>;
export type Person = z.infer<typeof Person>;
export type Rule = z.infer<typeof Rule>;
export type StatementFile = z.infer<typeof StatementFile>;
